import React, { createContext, useContext, useState, useEffect } from 'react';
import { USER_ROLES, normalizeRole, isLocalDev, DEV_ACCOUNTS, getDevAccountByCredentials } from '../config/authRoles';
import { authService } from '../services/authService';
import { merchantService } from '../services/merchantService';
import { AUTH_TOKEN_KEY } from '../services/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const rawRoles = parsedUser?.userRoles || (parsedUser?.roles ? [parsedUser.roles] : (parsedUser?.role ? [parsedUser.role] : []));
        const normalizedRoles = Array.isArray(rawRoles) ? rawRoles.map(r => normalizeRole(r)) : [normalizeRole(rawRoles)];
        
        const normalizedUser = {
          ...parsedUser,
          role: normalizedRoles[0] || '',
          roles: normalizedRoles
        };
        setUser(normalizedUser);
      } catch (error) {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const storeAuthResponse = (authResponse) => {
    const apiUser = authResponse.user;
    const rawRoles = apiUser.userRoles || (apiUser.roles ? [apiUser.roles] : []);
    const normalizedRoles = Array.isArray(rawRoles) ? rawRoles.map(r => normalizeRole(r)) : [normalizeRole(rawRoles)];
    
    const userData = {
      ...apiUser,
      name: [apiUser.firstName, apiUser.middleName, apiUser.lastName].filter(Boolean).join(' ') || apiUser.username,
      role: normalizedRoles[0] || '',
      roles: normalizedRoles,
    };

    localStorage.setItem(AUTH_TOKEN_KEY, authResponse.token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const loginAsDevAccount = (devAccount) => {
    const rawRoles = devAccount.userRoles || devAccount.roles || [devAccount.role];
    const normalizedRoles = Array.isArray(rawRoles) ? rawRoles.map(r => normalizeRole(r)) : [normalizeRole(rawRoles)];
    const combinedRoles = Array.from(new Set([...normalizedRoles, ...(devAccount.roles || [])]));

    const userData = {
      id: devAccount.id,
      userId: devAccount.id,
      username: devAccount.username,
      email: devAccount.email,
      firstName: devAccount.firstName,
      lastName: devAccount.lastName,
      name: devAccount.name,
      role: normalizedRoles[0] || devAccount.role,
      roles: combinedRoles,
      userRoles: combinedRoles,
      company: devAccount.company,
      designation: devAccount.designation,
      department: devAccount.department,
      org_id: devAccount.org_id,
      orgId: devAccount.orgId,
      mobile: '9876543210',
      status: 'ACTIVE',
      isDevUser: true
    };

    localStorage.setItem(AUTH_TOKEN_KEY, devAccount.token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const devLogin = (roleKey) => {
    if (!isLocalDev()) {
      return { success: false, error: 'Dev login is available in local development only.' };
    }
    const devAccount = DEV_ACCOUNTS[roleKey];
    if (!devAccount) {
      return { success: false, error: `Dev account for role "${roleKey}" not found.` };
    }
    const userData = loginAsDevAccount(devAccount);
    return { success: true, user: userData };
  };

  const login = async (username, password) => {
    if (isLocalDev()) {
      const devAccount = getDevAccountByCredentials(username, password);
      if (devAccount) {
        const userData = loginAsDevAccount(devAccount);
        return { success: true, user: userData };
      }
    }

    try {
      const authResponse = await authService.login({ username, password });
      return { success: true, user: storeAuthResponse(authResponse) };
    } catch (error) {
      return { success: false, error: error.userMessage || 'Login failed. Please try again.' };
    }
  };

  const signup = async (formData) => {
    try {
      await merchantService.create({
        orgName: formData.companyName,
        orgLegalName: formData.legalName,
        orgEmail: formData.companyEmail,
        address: formData.companyAddress,
        firstName: formData.managerFirstName,
        middleName: formData.managerMiddleName,
        lastName: formData.managerLastName,
        userEmail: formData.managerEmail,
        mobile: formData.managerMobile,
        employeeId: formData.employeeId,
        password: formData.password,
      });
      return login(formData.managerEmail, formData.password);
    } catch (error) {
      return { success: false, error: error.userMessage || 'Signup failed. Please try again.' };
    }
  };


  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setUser(null);
    }
  };

  const value = {
    user,
    role: user?.role || '',
    roles: user?.roles || [],
    isAdmin: user?.roles?.includes(USER_ROLES.SUPER_ADMIN) || user?.roles?.includes(USER_ROLES.ADMIN),
    isEmployee: user?.roles?.includes(USER_ROLES.EMPLOYEE),
    isReadOnly: user?.roles?.includes(USER_ROLES.TA_USER),
    isDev: isLocalDev(),
    hasRole: (requiredRoles) => {
      if (!user?.roles) return false;
      const rolesToCheck = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
      return rolesToCheck.some(r => user.roles.includes(r));
    },
    login,
    devLogin,
    signup,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
